import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Phone, MapPin, Mail, Instagram, Facebook, ChevronRight, Filter, X, Car } from 'lucide-react';
import Header from '../components/Header';
import CarCard from '../components/CarCard';
import ContactForm from '../components/ContactForm';
import Logo from '../components/Logo';
import FeaturedCarousel from '../components/FeaturedCarousel';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CARS_PER_PAGE = 9;

const HomePage = () => {
  const { settings } = useSettings();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: 'Todos',
    fuel: 'Todos',
    transmission: 'Todas',
    sort: 'newest'
  });

  useEffect(() => {
    setCurrentPage(1);
    fetchCars();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCars = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'Todos') params.append('status', filters.status);
      if (filters.fuel !== 'Todos') params.append('fuel', filters.fuel);
      if (filters.transmission !== 'Todas') params.append('transmission', filters.transmission);
      params.append('sort', filters.sort);

      const response = await axios.get(`${API_URL}/api/cars?${params.toString()}`);
      setCars(response.data);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = ['Todos', 'Disponível', 'Reservado', 'Vendido'];
  const fuelOptions = ['Todos', 'Gasóleo', 'Gasolina', 'Diesel', 'Híbrido', 'Elétrico'];
  const transmissionOptions = ['Todas', 'Manual', 'Automática'];
  const sortOptions = [
    { value: 'newest', label: 'Mais Recentes' },
    { value: 'price_asc', label: 'Preço (↑)' },
    { value: 'price_desc', label: 'Preço (↓)' },
    { value: 'km_asc', label: 'Km (↑)' },
    { value: 'km_desc', label: 'Km (↓)' },
    { value: 'year_desc', label: 'Ano (↓)' },
    { value: 'year_asc', label: 'Ano (↑)' }
  ];

  const clearFilters = () => {
    setFilters({
      status: 'Todos',
      fuel: 'Todos',
      transmission: 'Todas',
      sort: 'newest'
    });
  };

  const hasActiveFilters = filters.status !== 'Todos' || filters.fuel !== 'Todos' || filters.transmission !== 'Todas';

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Helmet>
        <title>ICarDetail Automóveis - Carros Usados em Agrela</title>
        <meta name="description" content="Stand de automóveis usados em Agrela, Santo Tirso. Viaturas usadas com garantia e financiamento. Compra e venda de carros usados. Visite o nosso stand ou contacte-nos." />
        <meta name="keywords" content="carros usados, stand automóveis, carros usados Agrela, carros usados Santo Tirso, viaturas usadas, compra venda automóveis, stand carros Porto, icardetail" />
        <meta property="og:title" content="ICarDetail Automóveis - Carros Usados em Agrela" />
        <meta property="og:description" content="Stand de automóveis usados em Agrela, Santo Tirso. Viaturas com garantia e financiamento disponível." />
        <meta property="og:image" content={`${window.location.origin}/logo-icon.png`} />
        <meta property="og:url" content={window.location.origin} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ICarDetail Automóveis" />
        <meta property="og:locale" content="pt_PT" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="ICarDetail Automóveis - Carros Usados em Agrela" />
        <meta name="twitter:description" content="Stand de automóveis usados em Agrela, Santo Tirso. Viaturas com garantia e financiamento." />
        <link rel="canonical" href={window.location.origin} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AutoDealer",
          "name": "ICarDetail Automóveis",
          "description": "Stand de automóveis usados em Agrela, Santo Tirso. Compra e venda de carros usados com garantia.",
          "url": window.location.origin,
          "logo": `${window.location.origin}/logo-icon.png`,
          "telephone": settings?.phone_intl || "+351919714805",
          "email": settings?.email || "info@icardetail.pt",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "R. 25 de Abril 908",
            "addressLocality": "Agrela",
            "addressRegion": "Santo Tirso",
            "postalCode": "4825-026",
            "addressCountry": "PT"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 41.3486,
            "longitude": -8.4738
          },
          "openingHours": settings?.schedule || "Mo-Fr 09:00-19:00, Sa 09:00-13:00",
          "priceRange": "€€",
          "areaServed": ["Agrela", "Santo Tirso", "Porto", "Braga"]
        })}</script>
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1710823367826-02e38b3c9f67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxibGFjayUyMGx1eHVyeSUyMHNwb3J0cyUyMGNhcnxlbnwwfHx8fDE3NzUyNTUzNDB8MA&ixlib=rb-4.1.0&q=85"
            alt="Luxury Car"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl animate-fade-in">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-zinc-500 mb-4">
              Stand de Automóveis
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-light text-white mb-6">
              Usados de<br />
              <span className="font-semibold">Confiança</span>
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed mb-8 max-w-lg">
              Compra e venda de viaturas usadas e semi-novas com garantia. Transparência, qualidade e um processo simples do primeiro contacto à entrega.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#stock"
                className="bg-white text-black font-semibold px-8 py-4 hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="view-stock-btn"
              >
                Ver Stock
                <ChevronRight size={20} />
              </a>
              <a 
                href="#contacto"
                className="border border-zinc-700 bg-transparent text-white px-8 py-4 hover:bg-zinc-800 transition-colors inline-flex items-center justify-center"
                data-testid="contact-btn"
              >
                Contactar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Carousel */}
      <FeaturedCarousel />

      {/* Stock Section */}
      <section id="stock" className="py-20 px-6 md:px-12" data-testid="stock-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-zinc-500 mb-2">
                O Nosso Stock
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white">
                Viaturas Disponíveis
              </h2>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3" data-testid="filters-container">
              <Filter size={18} className="text-zinc-500" />
              
              {/* Status Filter */}
              <div className="flex flex-wrap gap-2">
                {statusOptions.map(option => (
                  <button
                    key={option}
                    onClick={() => setFilters(f => ({ ...f, status: option }))}
                    className={`px-4 py-2 text-sm transition-colors ${
                      filters.status === option
                        ? 'bg-white text-black'
                        : 'bg-transparent text-zinc-400 border border-zinc-800 hover:border-zinc-600'
                    }`}
                    data-testid={`filter-status-${option.toLowerCase()}`}
                  >
                    {option}
                  </button>
                ))}
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm text-zinc-400 hover:text-white flex items-center gap-1"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} />
                  Limpar
                </button>
              )}
            </div>
          </div>

          {/* Secondary Filters Row */}
          <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-zinc-800">
            {/* Fuel Filter */}
            <select
              value={filters.fuel}
              onChange={(e) => setFilters(f => ({ ...f, fuel: e.target.value }))}
              className="bg-[#12141D] border border-zinc-800 text-zinc-300 px-4 py-2 text-sm focus:outline-none focus:border-zinc-600"
              data-testid="filter-fuel-select"
            >
              {fuelOptions.map(option => (
                <option key={option} value={option}>{option === 'Todos' ? 'Combustível' : option}</option>
              ))}
            </select>

            {/* Transmission Filter */}
            <select
              value={filters.transmission}
              onChange={(e) => setFilters(f => ({ ...f, transmission: e.target.value }))}
              className="bg-[#12141D] border border-zinc-800 text-zinc-300 px-4 py-2 text-sm focus:outline-none focus:border-zinc-600"
              data-testid="filter-transmission-select"
            >
              {transmissionOptions.map(option => (
                <option key={option} value={option}>{option === 'Todas' ? 'Caixa' : option}</option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={filters.sort}
              onChange={(e) => setFilters(f => ({ ...f, sort: e.target.value }))}
              className="bg-[#12141D] border border-zinc-800 text-zinc-300 px-4 py-2 text-sm focus:outline-none focus:border-zinc-600 ml-auto"
              data-testid="filter-sort-select"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Cars Grid */}
          {loading ? (
            <div className="text-center py-20">
              <p className="text-zinc-400">A carregar viaturas...</p>
            </div>
          ) : cars.length === 0 ? (
            <div className="text-center py-20" data-testid="no-cars-message">
              <Car size={48} className="mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-400">Nenhuma viatura encontrada com os filtros selecionados.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="cars-grid">
                {cars.slice((currentPage - 1) * CARS_PER_PAGE, currentPage * CARS_PER_PAGE).map((car, index) => (
                  <CarCard key={car.id} car={car} delay={index} />
                ))}
              </div>

              {/* Pagination */}
              {cars.length > CARS_PER_PAGE && (
                <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm font-medium border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="pagination-prev"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: Math.ceil(cars.length / CARS_PER_PAGE) }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 text-sm font-medium transition-colors ${
                        page === currentPage ? 'bg-red-600 text-white' : 'border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
                      }`}
                      data-testid={`pagination-page-${page}`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(cars.length / CARS_PER_PAGE), p + 1))}
                    disabled={currentPage === Math.ceil(cars.length / CARS_PER_PAGE)}
                    className="px-4 py-2 text-sm font-medium border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="pagination-next"
                  >
                    Seguinte
                  </button>
                </div>
              )}
            </>
          )}

          <p className="text-center text-zinc-500 mt-8">
            {cars.length} viatura(s) encontrada(s)
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 px-6 md:px-12 bg-[#12141D]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-zinc-500 mb-2">
                Contacto
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-6">
                Fale Connosco
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Estamos disponíveis para esclarecer todas as suas dúvidas. Entre em contacto connosco e respondemos o mais rápido possível.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#090A0F] flex items-center justify-center">
                    <Phone size={20} className="text-red-500" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Telemóvel</p>
                    <a href={`tel:${settings.phone_intl}`} className="text-white hover:text-red-500 transition-colors text-lg font-medium">
                      {settings.phone}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#090A0F] flex items-center justify-center">
                    <Mail size={20} className="text-red-500" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Email</p>
                    <a href={`mailto:${settings.email}`} className="text-white hover:text-red-500 transition-colors">
                      {settings.email}
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#090A0F] flex items-center justify-center">
                    <MapPin size={20} className="text-red-500" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Morada</p>
                    <p className="text-white">{settings.address_full}</p>
                  </div>
                </div>
              </div>
            </div>

            <ContactForm />
          </div>
        </div>
      </section>

      {/* Location Section with Google Maps */}
      <section className="py-20 px-6 md:px-12 bg-[#090A0F]" data-testid="location-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Info */}
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-red-500 mb-2">
                Localização
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-6">
                Onde Estamos
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Visite o nosso stand e conheça pessoalmente as nossas viaturas. Estamos localizados numa zona de fácil acesso com estacionamento disponível.
              </p>
              
              <div className="space-y-4 bg-[#12141D] p-6 border border-zinc-800">
                <div className="flex items-start gap-4">
                  <MapPin size={24} className="text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium mb-1">Morada</p>
                    <p className="text-zinc-400">{settings.address_line1}</p>
                    <p className="text-zinc-400">{settings.address_line2}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 pt-4 border-t border-zinc-800">
                  <Phone size={24} className="text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium mb-1">Telefone</p>
                    <a href={`tel:${settings.phone_intl}`} className="text-xl text-red-500 hover:text-red-400 font-semibold">
                      {settings.phone}
                    </a>
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-800">
                  <a 
                    href={settings.maps_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-semibold transition-colors"
                  >
                    <MapPin size={18} />
                    Abrir no Google Maps
                  </a>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="h-[400px] lg:h-auto min-h-[400px] bg-[#12141D] border border-zinc-800 overflow-hidden">
              <iframe
                src={settings.maps_embed || "https://maps.google.com/maps?q=ICarDetail+MotorSport,+R.+25+de+Abril+908,+4825-026+Agrela,+Portugal&t=&z=16&ie=UTF8&iwloc=B&output=embed"}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização ICARDETAIL"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-zinc-800 bg-[#12141D]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Logo size="default" />
            
            <div className="flex items-center gap-6">
              <a href={`tel:${settings.phone_intl}`} className="text-zinc-400 hover:text-red-500 transition-colors flex items-center gap-2">
                <Phone size={18} />
                <span>{settings.phone}</span>
              </a>
              {settings.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-white transition-colors" data-testid="social-instagram">
                  <Instagram size={20} />
                </a>
              )}
              {settings.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-white transition-colors" data-testid="social-facebook">
                  <Facebook size={20} />
                </a>
              )}
            </div>
            
            <p className="text-zinc-500 text-sm">
              © {new Date().getFullYear()} ICARDETAIL. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
